import { type ComponentChildren } from 'preact';
import { Suspense } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { DashLayout, type NavSection, type SidebarMode, type TopBarUser, type TopBarAccount, NotificationBell, SiteSwitcher, useConfirm, useViewportTier } from '@xantory/ui';
import { useAuthStore, useConfigStore, usePermissions, useThemeStore, useUiStore } from '@xantory/store';
import { useAckNotification, useFleetLive, useFleetOverview, useLogout, useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications, useOfflineQueueSync, useQueryClient, useRemoveAccount, useSwitchAccount, useSiteScope, useSites, useUnreadCount, useCropAlertSummary, unackedCropAlerts, useUnregisteredCount, useOpenP1Count } from '@xantory/api';
import { useT, useNavLabel } from '@xantory/i18n';
import { ROUTES, href, CommandMenu, SEARCH_SHORTCUT_LABEL, useOpenNotification, siteStatus } from '@xantory/features';
import * as stylex from '@stylexjs/stylex';
import '@tokens/colors.stylex';
import { colors } from '@tokens/colors.stylex';
import '@tokens/spacing.stylex';
import { spacing } from '@tokens/spacing.stylex';
import '@tokens/typography.stylex';
import { typography } from '@tokens/typography.stylex';
import { FALLBACK_ROUTE } from './default-route';

// R-238 §6 B2 — the app-wide critical bar. Full-bleed and above everything, because
// its whole purpose is to reach somebody who is looking at another screen.

import { RouteErrorBoundary } from './RouteErrorBoundary';
import { PageFallback } from './PageFallback';
interface ShellLayoutProps {
  activeRoute: string;
  /** Sidebar + breadcrumb come pre-resolved from the feature registry (Router owns it). */
  navSections: NavSection[];
  breadcrumb: readonly [string, string];
  children: ComponentChildren;
}
function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
export function ShellLayout({
  activeRoute,
  navSections,
  breadcrumb,
  children
}: ShellLayoutProps) {
  const tu = useT('ui');
  const tss = useT('siteStatus');
  const nl = useNavLabel();
  // R-145: one app-wide flush of the offline mutation queue (receiving, WO
  // complete, TO stage, incident notes) — reconnect + poll, invalidating each
  // flushed item's keys. Mounted once here so it runs on every authed route.
  useOfflineQueueSync();
  const qc = useQueryClient();
  // The topbar's force-refresh (R-239) is gone (user, 2026-10-16): every list page carries its own
  // refresh button now, so a global hammer in the bar is a second, vaguer way to do the same thing.
  // `TopBar`/`DashLayout` keep the optional `onRefresh` prop — absent means no button.
  // R-346 — the ⌘K palette. The listener lives HERE, on the shell, rather than
  // inside the palette: a shortcut owned by a component that is only mounted while
  // it is open could never be the thing that opens it, and one owned by a page
  // would stop working the moment the route changed.
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'k' || !(e.metaKey || e.ctrlKey) || e.altKey) return;
      // ⚠ Chrome's own Ctrl/⌘K focuses the address bar, so this MUST preventDefault
      // or the palette opens behind a browser UI that has just stolen the keyboard.
      e.preventDefault();
      setPaletteOpen(o => !o);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const user = useAuthStore(s => s.user);
  // POST /auth/logout, then clearSession (in the mutation's onSettled) → Router bounces to login
  // — or, with several accounts signed in, to whichever one is promoted in its place (R-303).
  const logout = useLogout();
  const {
    route
  } = useLocation();
  // An explicit logout lands on `/`. Without it the login form rendered at the page being left
  // (seen on production), and signing back in kept that deep link instead of the role landing.
  // `replace`, so Back does not return to that page. Session EXPIRY stays put on purpose — the
  // operator signs back in where they were. `mutateAsync`, not a `mutate` callback: clearSession
  // unmounts this shell, and TanStack drops per-call callbacks of an unmounted observer.
  const onLogout = () => {
    void logout.mutateAsync().catch(() => {}) // the session is cleared either way (useLogout's onSettled)
    .finally(() => route(ROUTES.home, true));
  };
  const accountMap = useAuthStore(s => s.accounts);
  const activeId = useAuthStore(s => s.activeId);
  const beginAddAccount = useAuthStore(s => s.beginAddAccount);
  const switchAccount = useSwitchAccount();
  const removeAccount = useRemoveAccount();
  const addToast = useUiStore(s => s.addToast);
  const confirm = useConfirm();
  const {
    resolved,
    setPreference
  } = useThemeStore();
  const {
    sidebarCollapsed,
    toggleSidebar,
    setSidebarCollapsed,
    drawerOpen,
    toggleDrawer,
    closeDrawer
  } = useUiStore();
  const deployMode = useConfigStore(s => s.mode);
  // The brand/logo links to the stable per-mode home (cloud → Overview) — the
  // same resting "home" the Router uses for `/` and unknown paths (R-147
  // follow-up). Role-based landing is a fresh-login one-shot, not the logo.
  const siteId = useConfigStore(s => s.siteId);
  const siteName = useConfigStore(s => s.siteName);
  const setSite = useConfigStore(s => s.setSite);
  const clearSite = useConfigStore(s => s.clearSite);
  // Cloud-only endpoint — kept dormant in local mode (the switcher only renders in cloud anyway).
  const {
    data: sitesData
  } = useSites(deployMode === 'cloud');
  // Fleet status, PUSHED over an authenticated WebSocket (D-137). One server-side
  // computation reaches every console, so this screen and /admin/sites can no
  // longer disagree between poll ticks — which is what made the same site read
  // `offline` on one and `critical` on the other.
  //
  // The REST read stays as the fallback, and it is the SAME payload the socket
  // pushes (`fleet_overview_payload` serves both): it covers the first paint before
  // the socket opens, and a client that cannot hold one at all.
  const live = useFleetLive(deployMode === 'cloud');
  const {
    data: polled
  } = useFleetOverview(deployMode === 'cloud' && live.state !== 'connected');
  const fleet = live.data ?? polled;
  const siteHealth = fleet?.sites.find(s => s.site_id === siteId)?.health;
  // Each dropdown row's dot reflects that site's health (same fleet-overview source as the top dot).
  // ⚠ A person homed to one farm is offered ONLY that farm. The gateways refuse
  // the others regardless (`infra_admission`), so leaving them in the list would
  // just be an invitation to a 403 — and would suggest a reach they do not have.
  // `undefined` home = headquarters, which sees everything.
  const homeSiteId = useAuthStore(st => st.user?.homeSiteId);
  const visibleSites = homeSiteId ? (sitesData?.sites ?? []).filter(s => s.id === homeSiteId) : sitesData?.sites ?? [];
  const switcherSites = visibleSites.map(s => {
    // Same derivation as the top dot and as /admin/sites — one mapping, three
    // places rendering it.
    const rowStatus = siteStatus({
      health: fleet?.sites.find(f => f.site_id === s.id)?.health,
      hasServer: true
    });
    return {
      id: s.id,
      name: s.name,
      tone: rowStatus.tone,
      // The bracketed word beside each row — health only (not the reachability
      // compound the current-site subtitle used to show): `list_servers` is
      // admin-only, so a non-admin cannot know reachability for sites other
      // than the one they're on.
      statusLabel: rowStatus.disabled ? tss('disabled') : rowStatus.healthKey ? tss(rowStatus.healthKey) : tss('unknown')
    };
  });
  // The store's `siteName` is only a CACHE (config.ts: "cached alongside the id so the
  // shell can show it offline"). Once the sites list has loaded, the SERVER is
  // authoritative — otherwise renaming a site leaves the topbar showing the old name
  // while the dropdown right below it already shows the new one, which is what a user
  // reported. Fall back to the cache only for first paint / offline.
  const liveSiteName = switcherSites.find(s => s.id === siteId)?.name;
  const displaySiteName = liveSiteName ?? siteName;
  // Refresh the cache when the server disagrees, so a reload does not resurrect the
  // stale name for the moment before the sites query resolves. Guarded on a real
  // difference so this cannot loop.
  useEffect(() => {
    if (siteId && liveSiteName && liveSiteName !== siteName) setSite(siteId, liveSiteName);
  }, [siteId, liveSiteName, siteName, setSite]);
  // A persisted `siteId` can outlive the site itself — removed from the fleet, or the
  // user's access revoked. Left dangling, the shell keeps addressing `/sites/{gone-id}`
  // and every request 404s while the switcher shows a site that no longer exists.
  // Clear it so the operator lands back on the picker.
  //
  // Guarded on a SUCCESSFUL list (`sitesData` present) rather than "not found": while
  // the query is loading or has errored there is no list to judge against, and
  // clearing on a network blip would eject someone mid-task for no reason.
  useEffect(() => {
    if (!siteId || !sitesData) return;
    if (!sitesData.sites.some(s => s.id === siteId)) clearSite();
  }, [siteId, sitesData, clearSite]);
  // One derivation for the dot and the word, shared with /admin/sites
  // (`shared/site-status.ts`) so the two screens cannot disagree again.
  //
  // The switcher shows the HEALTH axis only, and says so. It cannot show
  // reachability: `list_servers` is admin-only, so a non-admin has no way to know
  // whether the box is answering — and the old label put the health word where an
  // operator reads a reachability word, which is why "critical" here and "offline"
  // on the admin page looked like a contradiction.
  //
  // The "Cloud site" prefix is gone. It was hardcoded onto every site, so a LOCAL
  // site was labelled a cloud one.
  // Both axes now, from ONE payload (D-137). Before this the switcher could only
  // ever show health — `list_servers` is admin-gated, so reachability was invisible
  // to a non-admin. It rides on the fleet payload now, so every cloud user sees the
  // same two facts /admin/sites shows.
  const siteFleetRow = fleet?.sites.find(s => s.site_id === siteId);
  const siteStatusView = siteStatus({
    health: siteHealth,
    hasServer: true,
    online: siteFleetRow?.reachable
  });

  // Sidebar mode: the drawer is width-driven (phones have no room for a rail beside
  // content); everywhere else `sidebarCollapsed` decides, so the topbar hamburger is
  // live on tablet as well as desktop.
  //
  // ⚠ `mid` used to hard-code `'rail'`, which read `sidebarCollapsed` nowhere. The
  // hamburger still called `toggleSidebar()`, so on a tablet it flipped a flag that
  // nothing rendered — a control that could not do anything. Auto-collapse
  // is preserved by the effect below; what changed is that the operator can undo it.
  const tier = useViewportTier();
  const sidebarMode: SidebarMode = tier === 'narrow' ? 'drawer' : sidebarCollapsed ? 'rail' : 'full';

  // Entering the tablet tier collapses to the rail — 248px of nav beside content is too
  // much at that width — and LEAVING it puts the sidebar back. `autoCollapsed` is what
  // separates "the layout collapsed this" from "the operator collapsed this": only the
  // former is ever undone, so a deliberate collapse survives every resize.
  //
  // Keyed on the tier TRANSITION, not on every render — a resize-triggered re-collapse
  // would fight the click that just undid it.
  const [autoCollapsed, setAutoCollapsed] = useState(false);
  useEffect(() => {
    if (tier === 'mid') {
      if (!sidebarCollapsed) {
        setSidebarCollapsed(true);
        setAutoCollapsed(true);
      }
    } else if (autoCollapsed) {
      setSidebarCollapsed(false);
      setAutoCollapsed(false);
    }
    // Transition-only on purpose; the other values are read as of that transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);
  const onHamburger = () => {
    if (tier === 'narrow') return toggleDrawer();
    // An explicit choice outranks the automatic one — once the operator has touched
    // the toggle, leaving the tablet tier must not silently re-expand behind them.
    setAutoCollapsed(false);
    toggleSidebar();
  };
  const toggleTheme = () => setPreference(resolved === 'dark' ? 'light' : 'dark');

  // R-060: the bell — polled own-data notifications. Cloud-only, in both senses:
  // the inbox API is gateway-cloud's (`/users/*` isn't proxied at a site), so the
  // poll would fail every 30s AND the bell would sit there permanently empty —
  // a control that cannot ever do anything. At a site there is no bell at all.
  const {
    ready: siteReady
  } = useSiteScope();
  const {
    can
  } = usePermissions();
  const unregQ = useUnregisteredCount(siteReady && can('assets', 'read'));
  // R-238 §6 B2 — the prototype's APP-WIDE critical banner.
  //
  // ⚠ Deliberately not confined to the incidents list. A P1 is the one thing on this
  // console that must reach somebody who is looking at a completely different screen;
  // a banner that only appears where you were already going to look answers a
  // question nobody had. The prototype puts it above the top bar on every view and
  // gives it a way in.
  //
  // Gated exactly as the unregistered badge is, and for the same three reasons:
  // the maintenance read permission (pointing at a screen the reader cannot open is
  // noise), a site actually being selected (firing during the cloud no-site window
  // caches an error under a key that never changes when the site arrives), and a
  // slow poll — a P1 is not a per-second event and this rides every page load.
  const p1Q = useOpenP1Count(siteReady && can('maintenance', 'read'));
  const openP1 = p1Q.data ?? 0;
  // The Farm → Alerts leaf carries the crop alerts nobody has acknowledged, so
  // the sidebar says there is something to react to before the operator has to
  // go and look — the same job the bell's unread count does for notifications.
  //
  // Gated on the same three things as the badges above: the `farming` read
  // permission (a badge pointing at a screen the reader cannot open is noise),
  // a site actually being selected, and cloud mode — the alerts API is
  // gateway-cloud's, and the leaf itself is cloud-only for that reason.
  const cropAlertsQ = useCropAlertSummary(siteId ?? '', deployMode === 'cloud' && siteReady && can('farming', 'read'));
  const notifEnabled = deployMode === 'cloud';
  const notifQ = useNotifications(notifEnabled);
  const unreadQ = useUnreadCount(notifEnabled);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const ackMut = useAckNotification();
  // A bell row now GOES somewhere (the point of a notification). The shared hook
  // owns what "open" means — read receipt + site context — so the bell and the
  // inbox page can't drift; the anchor's href does the navigating.
  const openNotif = useOpenNotification();
  const bell = !notifEnabled ? undefined : <NotificationBell unread={unreadQ.data ?? 0} items={(notifQ.data ?? []).map(n => ({
    id: n.id,
    severity: n.severity,
    title: n.title,
    createdAt: n.created_at,
    read: n.read_at != null,
    acked: n.acked_at != null,
    href: openNotif.hrefFor(n)
  }))} onRead={id => markRead.mutate(id)} onAck={id => ackMut.mutate(id)} onReadAll={() => markAllRead.mutate()}
  // The bell stays presentational (it holds no site/read metadata) — resolve
  // the row back to its notification here, where the data actually lives.
  onOpen={id => {
    const n = (notifQ.data ?? []).find(x => x.id === id);
    if (n) openNotif.open(n);
  }} seeAllHref={ROUTES.notifications} />;

  // Cloud mode: show the current site + switch inline via the dropdown.
  const topbarActions = deployMode === 'cloud' ? <SiteSwitcher name={displaySiteName ?? tu('selectSite')} tone={siteStatusView.tone} sites={switcherSites} currentId={siteId}
  // Site-scoped query keys don't carry the site id — `resolvePath` rewrites
  // `/v1/*` to `/sites/{siteId}/v1/*` at the path layer, so switching sites
  // leaves the PREVIOUS site's rows cached under identical keys. Invalidate
  // on switch, or the operator reads site A's incidents under site B's name.
  onSelect={s => {
    setSite(s.id, s.name);
    qc.invalidateQueries();
  }} /> : undefined;

  // Manifests carry English labels; translate at this boundary (unknown labels pass through).
  const sections: NavSection[] = navSections.map(sec => ({
    ...sec,
    title: nl(sec.title),
    items: sec.items.map(it => it.kind === 'group' ? {
      ...it,
      label: nl(it.label),
      children: it.children.map(c => ({
        ...c,
        label: nl(c.label)
      }))
    } : {
      ...it,
      label: nl(it.label)
    })
  }));
  const crumb: readonly [string, string] = [nl(breadcrumb[0]), nl(breadcrumb[1])];

  // The HQ → Notifications leaf carries the same unread count as the bell, so the
  // sidebar tells the truth whichever surface the operator is looking at.
  //
  // R-235: Assets → Unregistered carries the pending-device queue, as the
  // prototype's sidebar does. Gated on THREE things, all of them load-bearing:
  //   · the assets permission — a badge is a claim, and pointing at a screen the
  //     reader may not open is noise
  //   · a site actually being selected — in cloud mode this is site-scoped, and
  //     firing it during the no-site window caches an error under a key that never
  //     changes when the site arrives (the fault that once hid "+ New request")
  //   · the count endpoint, never the list, because the list runs a
  //     match-suggestion query per device and this rides every page load
  const navBadges = {
    [ROUTES.notifications]: unreadQ.data ?? 0,
    [ROUTES.assetsUnregistered]: unregQ.data ?? 0,
    [ROUTES.planningAlerts]: unackedCropAlerts(cropAlertsQ.data)
  };
  const topbarUser: TopBarUser | undefined = user ? {
    name: user.email,
    role: titleCase(user.userType ?? 'User'),
    initials: user.email.slice(0, 2).toUpperCase()
  } : undefined;

  // R-303. A session restored from the old single-account store has no email until
  // the profile call lands, so fall back to the id rather than rendering a blank row.
  const accounts: TopBarAccount[] = Object.values(accountMap).map(a => ({
    id: a.user.id,
    name: a.user.email || a.user.id,
    role: titleCase(a.user.userType ?? 'User'),
    initials: (a.user.email || a.user.id).slice(0, 2).toUpperCase(),
    active: a.user.id === activeId
  }));

  // The switch validates the target against the server before flipping, so all three
  // outcomes are real and each gets its own sentence — an expired session
  // and a dropped connection are not the same news.
  // Signing out of an account is NOT the cheap, reversible half of switching: only the
  // active account is kept alive, so getting back in means a full sign-in with a TOTP
  // code. The two controls sit on adjacent rows in the same menu, and the destructive
  // one is a small icon — so it gets a confirmation and the switch does not.
  const onSignOutAccount = (id: string) => {
    const email = accountMap[id]?.user.email ?? id;
    void confirm({
      title: tu('signOutConfirmTitle'),
      message: tu('signOutConfirmMessage', {
        email
      }),
      confirmLabel: tu('signOutConfirmLabel'),
      danger: true
    }).then(ok => {
      if (ok) removeAccount(id);
    });
  };
  const onSwitchAccount = (id: string) => {
    void switchAccount(id).then(outcome => {
      if (outcome === 'expired') addToast({
        variant: 'warning',
        message: tu('accountExpired')
      });else if (outcome === 'offline') addToast({
        variant: 'danger',
        message: tu('accountOffline')
      });
    });
  };
  return <DashLayout navSections={sections} activeKey={activeRoute} navBadges={navBadges} breadcrumb={crumb} mode={sidebarMode} drawerOpen={drawerOpen} homeHref={FALLBACK_ROUTE[deployMode]} onHamburger={onHamburger} onCloseDrawer={closeDrawer} onOpenSearch={() => setPaletteOpen(true)} searchShortcutLabel={SEARCH_SHORTCUT_LABEL} topbarActions={topbarActions} topbarBell={bell} user={topbarUser} settingsHref={ROUTES.settings} onLogout={onLogout} accounts={accounts} onSwitchAccount={onSwitchAccount} onSignOutAccount={onSignOutAccount} onAddAccount={beginAddAccount} theme={resolved} onThemeToggle={toggleTheme}>
      {/*
        Stable, per-route content host. The routed page (a bare Fragment root) used to be diffed
        directly as `<main>`'s children — but `<main>` is re-rendered independently by the shell
        (notification poll, theme, cloud site effects) AND by the page's own state (tabs), and
        Preact's child reconciliation for a Fragment sitting directly under such a churned host can
        desync: it orphans the previous page's DOM and appends the new render instead of replacing
        it, so pages pile up (and survive navigation). Wrapping the content in ONE keyed host — a
        real DOM node Preact fully owns, `display:contents` so it adds no box — gives the reconciler
        a 1:1 anchor per route: same route re-renders patch in place; a route change unmounts the old
        subtree cleanly via the key. No layout change (`<main>` is not a flex/grid container).
       */}
      <CommandMenu open={paletteOpen} onClose={() => setPaletteOpen(false)} sections={sections} activeKey={activeRoute} />
      {openP1 > 0 && <a href={href(ROUTES.maintenanceIncidents)} className="x78zum5 x6s0dn4 x1mnnc3i x3pdpzx x4qlrbh xahmsqx x2pyw9z x1hl2dhg x1ypdohk">
          <strong>{tu('criticalBannerCount', {
          count: String(openP1)
        })}</strong>
          <span>{tu('criticalBannerOpen')}</span>
        </a>}
      <div key={activeRoute} style={{
      display: 'contents'
    }}>
        <RouteErrorBoundary>
          {/* Pages are lazy-loaded (per-route code-split) — show a spinner in the content area while
              the chunk loads; the sidebar/top-bar stay put. */}
          <Suspense fallback={<PageFallback />}>{children}</Suspense>
        </RouteErrorBoundary>
      </div>
    </DashLayout>;
}